import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { phaseApi, featureApi } from '../../api/endpoints';
import { notify } from '../../components/Notifier';
import { useProjects } from '../../context/ProjectContext';
import { BugFormDialog } from './BugFormDialog';
import type { Bug, Feature, Phase } from '../../types';

/** Bỏ dấu tiếng Việt + hạ chữ thường để so khớp gần đúng, không phân biệt có dấu/không dấu. */
function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}

interface AddBugDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (bug: Bug) => void;
}

/**
 * Chọn Dự án → Giai đoạn → Chức năng (có tìm theo tên) rồi mới mở form thêm
 * lỗi — cho phép thêm lỗi thẳng từ /bugs mà không cần vào màn chi tiết tính
 * năng trước (vốn là luồng duy nhất trước đây).
 */
export function AddBugDialog({ open, onClose, onSaved }: AddBugDialogProps): JSX.Element {
  const { projects } = useProjects();

  const [projectId, setProjectId] = useState('');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [phaseId, setPhaseId] = useState('');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);

  useEffect(() => {
    if (!open) return;
    // Chỉ 1 dự án thì chọn luôn, khỏi bắt chọn thêm 1 bước thừa.
    setProjectId(projects.length === 1 ? projects[0].id : '');
    setPhaseId('');
    setFeatures([]);
    setSearch('');
    setSelectedFeature(null);
  }, [open, projects]);

  useEffect(() => {
    if (!projectId) {
      setPhases([]);
      setPhaseId('');
      return;
    }
    let active = true;
    phaseApi
      .listByProject(projectId, { limit: 200 })
      .then((result) => {
        if (!active) return;
        setPhases(result.items);
        setPhaseId(result.items.length === 1 ? result.items[0].id : '');
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!phaseId) {
      setFeatures([]);
      return;
    }
    let active = true;
    setLoadingFeatures(true);
    featureApi
      .listByPhase(phaseId, { limit: 200 })
      .then((result) => {
        if (active) setFeatures(result.items);
      })
      .catch(() => {
        if (active) notify('Không tải được danh sách chức năng', 'warning');
      })
      .finally(() => {
        if (active) setLoadingFeatures(false);
      });
    return () => {
      active = false;
    };
  }, [phaseId]);

  const filteredFeatures = useMemo(() => {
    const q = normalizeForSearch(search.trim());
    if (!q) return features;
    return features.filter((f) => normalizeForSearch(f.name).includes(q));
  }, [features, search]);

  return (
    <>
      <Dialog open={open && !selectedFeature} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>Thêm lỗi — chọn chức năng</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="add-bug-project-label">Dự án</InputLabel>
              <Select
                labelId="add-bug-project-label"
                label="Dự án"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!projectId}>
              <InputLabel id="add-bug-phase-label">Giai đoạn</InputLabel>
              <Select
                labelId="add-bug-phase-label"
                label="Giai đoạn"
                value={phaseId}
                onChange={(e) => setPhaseId(e.target.value)}
              >
                {phases.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Tìm chức năng"
              placeholder="Nhập tên chức năng…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!phaseId}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Xoá tìm kiếm, hiện tất cả" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={phaseId ? `Để trống để xem tất cả ${features.length} chức năng của giai đoạn` : undefined}
            />
            {loadingFeatures ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : !phaseId ? (
              <Typography variant="body2" color="text.secondary">
                Chọn dự án và giai đoạn để xem danh sách chức năng.
              </Typography>
            ) : features.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Giai đoạn này chưa có chức năng nào.
              </Typography>
            ) : filteredFeatures.length === 0 ? (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  Không tìm thấy chức năng khớp với "{search}".
                </Typography>
                <Button size="small" onClick={() => setSearch('')}>
                  Hiện tất cả {features.length} chức năng
                </Button>
              </Stack>
            ) : (
              <List
                dense
                sx={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  py: 0,
                }}
              >
                {filteredFeatures.map((f, i) => (
                  <ListItemButton
                    key={f.id}
                    onClick={() => setSelectedFeature(f)}
                    divider={i < filteredFeatures.length - 1}
                  >
                    <ListItemText primary={f.name} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Huỷ</Button>
        </DialogActions>
      </Dialog>

      {selectedFeature && (
        <BugFormDialog
          open={open}
          featureId={selectedFeature.id}
          featureName={selectedFeature.name}
          bug={null}
          onClose={onClose}
          onSaved={(saved) => {
            setSelectedFeature(null);
            onSaved(saved);
          }}
        />
      )}
    </>
  );
}
